'use client';
import { useEffect } from 'react';
import { useCurrentBranding } from '@/hooks/use-current-branding';

function hexToHsl(hex?: string) {
  // Ensuring the hex color string is valid
  if (!hex) {
    throw new Error('hexToHsl: received empty value');
  }
  if (!/^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(hex)) {
    throw new Error('Invalid HEX color.');
  }

  // Convert hex to RGB
  let r: number = 0,
    g: number = 0,
    b: number = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  r /= 255;
  g /= 255;
  b /= 255;

  // Find min and max values
  const cmin = Math.min(r, g, b),
    cmax = Math.max(r, g, b),
    delta = cmax - cmin;
  let h: number = 0,
    s: number = 0,
    l: number = 0;

  // Calculate hue
  if (delta === 0) h = 0;
  else if (cmax === r) h = ((g - b) / delta) % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  // Calculate lightness
  l = (cmax + cmin) / 2;

  // Calculate saturation
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return `${h} ${s}% ${l}%`;
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding] = useCurrentBranding();
  useEffect(() => {
    const doc = document.querySelector(':root') as HTMLElement;
    if (!doc) {
      return;
    }
    try {
      doc.style.setProperty(
        '--primary',
        `${hexToHsl(branding?.branding?.colors?.primary)}`
      );
    } catch {
      doc?.style.removeProperty('--primary');
    }
  }, [branding?.branding?.colors?.primary]);
  return children;
}
